import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    VisualComponent,
    VisualComponentId
} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {IAnimationData} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/animation/AnimationTypes';
import {
    ComponentDesc,
    IComponentDescSnapshot
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {RepeaterKey} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {ViewportUtil} from '@mintdata-wsp/common/util/ViewportUtil';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {BoxHostId, BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export enum BoxTypes
{
    VisualComponent = 'VisualComponent',
    Viewport = 'Viewport',
}

export interface IBoxSnapshot
{
    id:BoxId;
    parent:BoxId;
    children:Array<BoxId>;
    hostId:BoxHostId;
    componentId:VisualComponentId;
    componentDesc:IComponentDescSnapshot;
    boxType:BoxTypes;
}

export type AnimationValueMap = {[property:string]:number | string};

export interface IAnimationRef
{
    animation:any;
    promiseResolver:any;
}

export class BoxUiState
{
    @observable
    public ref:HTMLElement | null = null;

    @observable
    public repeatedRefs:Map<RepeaterKey, HTMLElement | null> = new Map();

    @observable
    public repeatedHostRefs:Map<string, HTMLElement | null> = new Map();

    @observable
    public hostRefs:Map<BoxHostId, HTMLElement | null> = new Map();

    public readonly animationData:Map<string, IAnimationData> = new Map<string, IAnimationData>();
}

export class Box
{
    public id:BoxId;

    public parent:Box;

    @observable
    public children:Array<Box>;

    @observable
    public hostId:BoxHostId;

    @observable
    public componentDesc:ComponentDesc;

    public component:VisualComponent;

    public boxType:BoxTypes;

    public page:Page;

    public uiState:BoxUiState = new BoxUiState();

    private constructor()
    {
    }

    public static createVisualBox(component:VisualComponent,
                                  page:Page,
                                  parent:Box = null,
                                  children:Array<Box> = [],
                                  hostId:BoxHostId = null):Box
    {
        const box:Box = new Box();

        box.id = IdUtil.newGUID();
        box.page = page;

        box.boxType = BoxTypes.VisualComponent;
        box.component = component;

        box.parent = parent;
        box.children = children;
        box.hostId = hostId;

        box.componentDesc = ComponentDesc.create(box);

        return box;
    }

    public static createViewport(page:Page, children:Array<Box> = []):Box
    {
        const box:Box = new Box();

        box.id = IdUtil.newGUID();
        box.page = page;

        box.boxType = BoxTypes.Viewport;
        box.component = ViewportUtil.getViewportVisualComponent();

        box.parent = null;
        box.children = children;
        box.hostId = null;

        box.componentDesc = ComponentDesc.create(box);

        box.componentDesc.layoutLeftProp.setInnerValue(Units.fromPixels(350));
        box.componentDesc.layoutTopProp.setInnerValue(Units.fromPixels(100));
        box.componentDesc.layoutWidthProp.setInnerValue(Units.fromPixels(1024));
        box.componentDesc.layoutHeightProp.setInnerValue(Units.fromPixels(768));
        box.componentDesc.nameProp.setInnerValue(AString.fromString('Viewport'));

        return box;
    }

    @computed
    public get bounds():Bounds
    {
        const {layoutLeft, layoutTop, layoutHeight, layoutWidth} = this.componentDesc;

        return Bounds.from(
            layoutLeft.value.value,
            layoutTop.value.value,
            layoutWidth.value.value,
            layoutHeight.value.value
        );
    }

    @computed
    public get absoluteBounds():Bounds
    {
        if( !this.bounds )
            return null;

        let x = this.bounds.x;
        let y = this.bounds.y;

        let parent = this.parent;

        while( parent )
        {
            x += parent.bounds.x;
            y += parent.bounds.y;

            // MAM-1597-6
            if( parent.component.name === 'Tabs' ) // TODO: ??? !!!
            {
                y += 50;
            }

            // MAM-1597-7
            if( parent.component.name === 'Box' ) // TODO: ??? !!!
            {
                const {model} = parent.componentDesc.propsValues;
                const {border, padding} = model;
            
                if( border.borderColor.visibility.value )
                {
                    x += border.borderThickness.value.value + padding.left.value.value;
                    y += border.borderThickness.value.value + padding.top.value.value;
                }
            }

            parent = parent.parent;
        }

        return Bounds.from(
            x,
            y,
            this.bounds.width,
            this.bounds.height
        );
    }

    public clone(page:Page, parent:Box):Box
    {
        const box:Box = new Box();

        box.id = IdUtil.newGUID();
        box.page = page;
        box.parent = parent;
        box.children = [];
        box.hostId = this.hostId;
        box.boxType = this.boxType;
        box.component = this.component;
        box.componentDesc = this.componentDesc.clone(box);

        return box;
    }

    @action.bound
    public setParent(box:Box):void
    {
        this.parent = box ? box : null;
    }

    @action.bound
    public setHostId(hostId:BoxHostId):void
    {
        this.hostId = hostId;
    }

    @action.bound
    public addChildren(boxes:Box[]):void
    {
        boxes.forEach(this.addChild);
    }

    @action.bound
    public addChild(box:Box, insertIndex:number = null):void
    {
        if( !box )
        {
            console.warn('Attempted to add undefined box as a child of', this.id);
            return;
        }
        
        if( box.parent )
            box.parent.removeChild(box);

        box.setParent(this);

        if( insertIndex === null )
            this.children.push(box);
        else
            this.children.splice(insertIndex, 0, box);
    }

    @action.bound
    public removeChild(box:Box):void
    {
        const idx = this.children.findIndex(child => (child.id === box.id));

        if( idx !== -1 )
        {
            box.setParent(null);
            this.children.splice(idx, 1);
        }
    }

    @action.bound
    public moveChild(childBox:Box, toIndex:number):void
    {
        if( !childBox )
        {
            console.warn('Attempted to move undefined box as a child of', this.id);
            return;
        }

        const boxIndex = this.children.findIndex(({id}) => id === childBox.id);
        const isToFirst:boolean = toIndex === 0;
        const isToLast:boolean = toIndex === this.children.length - 1;

        this.children.splice(boxIndex, 1);

        if( isToFirst )
            this.children.unshift(childBox as any);
        else if( isToLast )
            this.children.push(childBox as any);
        else
            this.children.splice(toIndex, 0, childBox as any);
    }

    @action.bound
    public replaceChildren(children:Array<Box>):void
    {
        (this.children as any).replace(children);
    }

    public get isViewport():boolean
    {
        return this.boxType === BoxTypes.Viewport;
    }

    public static toJson:ITransformer<Box, IBoxSnapshot> = createTransformer((box:Box) =>
    {
        return {
            id: box.id,
            parent: box.parent ? box.parent.id : null,
            children: box.children.filter(child => !!child).map(({id}) => id),
            hostId: box.hostId,
            componentId: box.component.id,
            componentDesc: ComponentDesc.toJson(box.componentDesc),
            boxType: box.boxType
        };
    });

    public static fromJson(json:IBoxSnapshot,
                           page:Page,
                           currentState?:Box,
                           currentJson?:IBoxSnapshot,
                           ctx?:DesignStateFromJsonCtx):Box
    {
        if( !currentState )
            return Box.fromJsonDeep(json, page);

        if( !currentJson )
            currentJson = Box.toJson(currentState);

        const box:Box = new Box();

        box.id = json.id;

        if( json.parent )
        {
            if( json.parent === currentJson.parent )
                box.parent = currentState.parent;
            else
            {
                const parent:Box = new Box();
                parent.id = json.parent;

                box.parent = parent;
            }
        }

        if( json.children === currentJson.children )
            box.children = currentState.children;
        else
        {
            box.children = json.children.map((boxId:BoxId) =>
            {
                const box:Box = new Box();
                box.id = boxId;

                return box;
            });
        }

        box.boxType = BoxTypes[json.boxType];
        box.hostId = json.hostId;

        box.component = rootState.ui.visualDesigner.artifacts.visualComponents.get(json.componentId);

        box.page = page;
        box.componentDesc = ComponentDesc.fromJson(json.componentDesc, box, currentState.componentDesc, currentJson.componentDesc, ctx);

        Box._restoreUiState(box, currentState);

        return box;
    }

    public static fromJsonDeep(json:IBoxSnapshot, page:Page):Box
    {
        const box:Box = new Box();

        box.id = json.id;

        if( json.parent )
        {
            const parent:Box = new Box();
            parent.id = json.parent;

            box.parent = parent;
        }

        box.children = json.children.map((boxId:BoxId) =>
        {
            const box:Box = new Box();
            box.id = boxId;

            return box;
        });

        box.boxType = BoxTypes[json.boxType];
        box.hostId = json.hostId;

        box.component = rootState.ui.visualDesigner.artifacts.visualComponents.get(json.componentId);

        box.page = page;
        box.componentDesc = ComponentDesc.fromJsonDeep(json.componentDesc, box);

        return box;
    }

    private static _restoreUiState(state:Box, currentState:Box):void
    {
        state.uiState = currentState.uiState;
    }
}
